import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, Check, Link2, ShieldCheck, Star, Trophy, UsersRound, UserRound, GitMerge, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PLAY_STORE_URL } from "@/components/InstallAppButton";
import { useNotificationCenter, type CenterItem } from "@/hooks/useNotificationCenter";

type Filter = "all" | "action" | "update";

const icons = {
  invitation: Link2,
  report: ShieldCheck,
  primary_club: UsersRound,
  elo_visibility: Trophy,
  play_review: Star,
  profile_completion: UserRound,
  opponent_duplicates: GitMerge,
  club_review: MapPin,
  club_duplicates: UsersRound,
};

export default function NotificationCenterList({ compact = false, onNavigate }: { compact?: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { items, unreadCount, isLoading, markRead, markReportOpened, markPlayReviewOpened, saving } =
    useNotificationCenter();
  const [filter, setFilter] = useState<Filter>("all");
  const visible = items.filter((item) => filter === "all" || item.group === filter);
  const subset = compact ? visible.slice(0, 4) : visible;

  const open = (item: CenterItem) => {
    onNavigate?.();
    if (item.kind === "report" && item.sharedMatchId) markReportOpened(item.sharedMatchId);
    if (item.kind === "play_review") {
      markPlayReviewOpened();
      window.location.assign(PLAY_STORE_URL);
      return;
    }
    if (item.dismissible && item.unread) {
      void markRead(item.key).catch(() => toast.error(t("common.error", { defaultValue: "Could not update notification" })));
    }
    navigate(item.href);
  };

  const dismiss = (item: CenterItem) => {
    void markRead(item.key, true)
      .catch(() => toast.error(t("common.error", { defaultValue: "Could not update notification" })));
  };

  return (
    <div className="space-y-3" data-testid="notification-center">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Bell className="h-4 w-4 shrink-0 text-primary" />
          <h2 className="font-semibold text-base">{t("notificationCenter.title")}</h2>
        </div>
        {unreadCount > 0 && (
          <span className="text-xs text-muted-foreground" aria-live="polite">
            {t("notificationCenter.unreadCount", { count: unreadCount })}
          </span>
        )}
      </div>
      <div role="group" aria-label={t("notificationCenter.title")} className="flex gap-1 rounded-md bg-muted/40 p-1">
        {(["all", "action", "update"] as const).map((value) => (
          <Button
            key={value}
            type="button"
            size="sm"
            variant={filter === value ? "secondary" : "ghost"}
            className="min-w-0 flex-1 px-1 text-xs h-8"
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
          >
            {t(`notificationCenter.${value === "action" ? "actions" : value === "update" ? "updates" : "all"}`)}
          </Button>
        ))}
      </div>
      {subset.length === 0 ? (
        <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground" role="status">
          {isLoading ? t("common.loading") : t(filter === "all" ? "notificationCenter.empty" : "notificationCenter.emptyFiltered")}
        </div>
      ) : (
        <div className="divide-y divide-border/70 rounded-xl border border-border/60 overflow-hidden">
          {subset.map((item) => {
            const Icon = icons[item.kind];
            return (
              <div key={item.key} className={`flex items-start gap-2 p-3 ${item.unread ? "bg-primary/[0.04]" : ""}`}>
                <div className="mt-1 shrink-0 rounded-lg bg-primary/10 p-2 text-primary" aria-hidden="true">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <button
                    type="button"
                    className="block w-full text-left hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary rounded"
                    onClick={() => open(item)}
                  >
                    <span className="flex items-center gap-1.5">
                      {item.unread && <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" aria-hidden="true" />}
                      <span className="text-sm font-semibold break-words">{t(item.titleKey)}</span>
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground break-words">
                      {t(item.bodyKey, item.values)}
                    </span>
                    <span className="mt-1.5 block text-xs font-semibold text-primary">
                      {t(item.kind === "play_review" ? "notificationCenter.review" : "notificationCenter.open")} →
                    </span>
                  </button>
                  {item.dismissible && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {item.unread && (
                        <Button size="sm" variant="ghost" className="h-7 text-[11px] px-2"
                          disabled={saving} onClick={() => void markRead(item.key)
                            .catch(() => toast.error(t("common.error", { defaultValue: "Could not update notification" })))}>
                          <Check className="h-3 w-3 mr-1" />{t("notificationCenter.markRead")}
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 text-[11px] px-2 text-muted-foreground"
                        disabled={saving} onClick={() => dismiss(item)}>
                        <X className="h-3 w-3 mr-1" />{t("notificationCenter.dismiss")}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {compact && (
        <Button type="button" variant="outline" size="sm" className="w-full"
          onClick={() => { onNavigate?.(); navigate("/app/notifications"); }}>
          {t("notificationCenter.viewAll")}
        </Button>
      )}
    </div>
  );
}
